/**
 * Number and quantity formatting for command summaries.
 *
 * Summaries are part of the {@link Changeset} public contract — the assistant
 * emits them, the diff cards render them, the undo menu labels itself with them
 * — so they must be **deterministic**: no locale, no `Intl`, no timezone. These
 * helpers always speak canonical units (PLAN §5); presenting a summary in the
 * user's preferred units is the UI's job, using `settings.units`.
 */

/** A number with at most `maxDecimals` decimals and no trailing zeros. */
export function num(value: number, maxDecimals = 3): string {
  if (!Number.isFinite(value)) return String(value);
  const fixed = value.toFixed(maxDecimals);
  return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
}

/** A mass in kilograms, rendered in grams below 1 kg. */
export function mass(kg: number): string {
  return kg < 1 ? `${num(kg * 1000, 1)} g` : `${num(kg, 3)} kg`;
}

/** A volume in litres. */
export function volume(litres: number): string {
  return `${num(litres, 2)} L`;
}

/** A percentage, e.g. `12.5 %`. */
export function percent(value: number): string {
  return `${num(value, 2)} %`;
}

/** A temperature in Celsius. */
export function temperature(celsius: number): string {
  return `${num(celsius, 1)} °C`;
}

/** A duration in minutes. */
export function minutes(value: number): string {
  return `${num(value, 1)} min`;
}

/**
 * Dry-hop contact. Stored as days; a non-whole value is a short hop in hours
 * (`0.5` → `12 h`).
 */
export function contact(days: number): string {
  const wholeDays = Math.abs(days - Math.round(days)) < 1e-9;
  return wholeDays ? `${num(days, 0)} d` : `${num(days * 24, 0)} h`;
}

/** Quote a name for a summary line. */
export function quoted(value: string): string {
  return `"${value}"`;
}

/** `old → new`, the shape a diff card wants. */
export function transition(before: string, after: string): string {
  return `${before} → ${after}`;
}
